'use client';

import React, { ReactNode, RefCallback } from 'react';

import { classNames } from '@bytel/product-wall/helpers';
import {
    Alignable,
    Box,
    Column,
    Columns,
    Container,
    Icon,
    IconName,
    IconSize,
    Text,
    TextLevels,
    TypographyBold,
    TypographyColor,
    View,
} from '@bytel/trilogy-react';

import { ContractType } from '@app-types/contract';
import { MobilePlanType } from '@app-types/plan';
import { ProductChildFull, ProductType } from '@app-types/product';
import { UserType } from '@app-types/user';

import { InlinePrice } from '@components/inline-price';

import { isComptant } from '@helpers/funding';
import { capitalize } from '@helpers/wording';

import { PlanRange } from '@constants/plan';

import { ProductCTA, SkeletonSticky } from '@product-detail/components';
import { PricesDetails } from '@product-detail/helpers';
import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

export type ProductStickyBoxProps = {
    currentProduct: ProductChildFull;
    currentPlan?: MobilePlanType;
    openShopAvailabilityModal: ClickEvent;
    isTicketShowed: boolean;
    isRenewal: boolean;
    renewalRedirectWall?: boolean;
    user?: UserType;
    prices: PricesDetails;
    contract?: ContractType;
    isLoading: boolean;
    showRef: RefCallback<HTMLDivElement>;
    dataCy?: string;
};

export function ProductStickyBox({
    currentProduct,
    currentPlan,
    openShopAvailabilityModal,
    isTicketShowed,
    renewalRedirectWall,
    isRenewal,
    user,
    prices,
    contract,
    isLoading,
    showRef,
}: ProductStickyBoxProps) {
    const { parentTitle, type } = currentProduct;
    const { productPrices, planPrices, funding } = prices;

    const generateProductSubtitle = () => {
        if (funding && !isComptant(funding.type)) {
            return (
                <>
                    {'+ '}
                    <InlinePrice
                        typo={[]}
                        level={TextLevels.THREE}
                        amount={productPrices.fundingMonthlyAmount}
                        period={`mois x${funding.installmentCount} mois`}
                    />
                </>
            );
        }
        return `\u00A0`;
    };

    if (isLoading || productPrices.marketingAmount <= 0) {
        return (
            <SkeletonSticky isTicketShowed={isTicketShowed} isPhone={type === ProductType.PHONE} showRef={showRef} />
        );
    }
    return (
        <>
            <Container
                className={classNames('sticky-box is-sticky-top is-backgroundless', isTicketShowed ? 'hidden' : '')}
            >
                <Box>
                    <Columns mobile verticalAlign={Alignable.ALIGNED_CENTER} marginless>
                        <Column>
                            <View className='gap-huge is-flex-tablet'>
                                <StickyItem
                                    icon={
                                        currentProduct.type === ProductType.PHONE
                                            ? IconName.MOBILE
                                            : IconName.ACCESSORIES
                                    }
                                    title={
                                        <>
                                            {parentTitle} à{' '}
                                            <InlinePrice
                                                typo={[]}
                                                amount={productPrices.marketingAmount}
                                                data-cy='sticky-product-price'
                                            />
                                        </>
                                    }
                                    subtitle={currentPlan ? generateProductSubtitle() : undefined}
                                />
                                {currentPlan && (
                                    <StickyItem
                                        icon={IconName.SIM_CARD}
                                        title={
                                            <>
                                                {`Forfait ${currentPlan.commercialRange === PlanRange.B_YOU ? 'B&you ' : ''}${isRenewal ? currentPlan.renewalLabelDataEnvelope : currentPlan.dataEnvelope || ''}`.trim()}{' '}
                                                à{' '}
                                                <InlinePrice
                                                    typo={[]}
                                                    amount={planPrices.mainAmount}
                                                    data-cy='sticky-plan-final-price'
                                                />
                                            </>
                                        }
                                        subtitle={
                                            planPrices.promoDuration && planPrices.promoDuration > 0 ? (
                                                <>
                                                    Pendant {planPrices.promoDuration} mois puis&nbsp;
                                                    <InlinePrice
                                                        typo={[]}
                                                        level={TextLevels.THREE}
                                                        amount={planPrices.forever}
                                                        data-cy='sticky-plan-forever-price'
                                                    />
                                                    &nbsp;- {capitalize(currentPlan.obligationLabel)}
                                                </>
                                            ) : (
                                                capitalize(currentPlan.obligationLabel)
                                            )
                                        }
                                        data-cy='sticky-plan'
                                    />
                                )}
                            </View>
                        </Column>
                        <ProductCTA
                            currentProduct={currentProduct}
                            openShopAvailabilityModal={openShopAvailabilityModal}
                            plan={currentPlan}
                            mobileIcon
                            contract={contract}
                            user={user}
                            isRenewal={isRenewal}
                            renewalRedirectWall={renewalRedirectWall}
                        />
                    </Columns>
                </Box>
            </Container>
            <div ref={showRef} />
        </>
    );
}

function StickyItem({ icon, title, subtitle, ...props }: { icon: IconName; title: ReactNode; subtitle?: ReactNode }) {
    return (
        <View
            flexable
            align={Alignable.ALIGNED_CENTER}
            style={{ maxWidth: '40rem' }}
            className='gap-medium w-fit'
            {...props}
        >
            <Icon name={icon} size={IconSize.MEDIUM} className='is-hidden-tablet-only w-fit' />
            <div>
                <Text marginless typo={[TypographyBold.TEXT_WEIGHT_SEMIBOLD, TypographyColor.TEXT_MAIN]}>
                    {title}
                </Text>
                {subtitle && (
                    <Text level={TextLevels.THREE} marginless>
                        {subtitle}
                    </Text>
                )}
            </div>
        </View>
    );
}
